/**
 * Ambient per-session correlation ID.
 *
 * Intentionally lives in its own module (not inside `services/logger`) so
 * the identifier can be produced, read and cleared without pulling any
 * logger code into the surface area. The API layer imports from here for
 * outbound HTTP headers; the logger will consume it in a later PR without
 * needing this module to depend on the logger.
 *
 * Contract:
 * - One value per app-launch/login. Generated by `initCorrelationId()`
 *   during the post-login flow (see web `GlobalOutlet`).
 * - Empty string when unset — callers treat that as "no header".
 * - Synchronous, non-throwing accessors so the API base query hot path
 *   can read it without try/catch.
 */

let currentCorrelationId = '';

/**
 * Generate a fresh UUID v4 via the platform's cryptographically secure
 * random source. Available on all runtimes this app targets:
 * - Web (Chrome/Safari/Firefox 2021+)
 * - React Native ≥ 0.71 with Hermes via `globalThis.crypto`
 *
 * Returns `undefined` when `randomUUID` is unavailable rather than falling
 * back to `Math.random` (CodeQL correctly flags that as insecure) and
 * rather than throwing (callers on the API/auth hot path must not crash).
 * A `undefined` result from here means the header will simply be omitted
 * on outbound requests — partial observability instead of a broken app.
 */
const generateUuid = (): string | undefined => {
    const cryptoRef = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (!cryptoRef?.randomUUID) {
        return undefined;
    }
    try {
        return cryptoRef.randomUUID();
    } catch {
        // Defensive: `randomUUID` throwing at runtime is exotic (e.g. some
        // insecure-context iframes) but must not take down login.
        return undefined;
    }
};

/**
 * Generate and store a fresh correlation ID. Call once during post-login
 * bootstrap. Overwrites any previous value. Returns the new ID, or `''`
 * when the runtime cannot generate one (see {@link generateUuid}) — the
 * empty string keeps the module's "empty = no header" contract intact.
 */
export const initCorrelationId = (): string => {
    currentCorrelationId = generateUuid() ?? '';
    return currentCorrelationId;
};

/**
 * Read the current correlation ID. Returns an empty string when unset so
 * callers can decide whether to attach it as a header without needing a
 * null-check.
 */
export const getCorrelationId = (): string => currentCorrelationId;

/**
 * Explicitly set the correlation ID. Exposed for tests and for callers
 * that inherit an ID from an inbound context (e.g. deep-link).
 */
export const setCorrelationId = (id: string): void => {
    currentCorrelationId = id;
};

/** Clear the correlation ID (e.g. on logout). */
export const clearCorrelationId = (): void => {
    currentCorrelationId = '';
};
